import { useMemo, useState } from 'react'
import { useVarianceData } from '../hooks/useVarianceData.js'
import { varianceTier } from '../lib/variance.js'
import { formatMoney, formatQty } from '../lib/format.js'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'

// Theoretical-vs-Actual variance report.
//
// For a date range the manager picks (default last 7 days), shows
// per-item depletion variance: what recipes SHOULD have used (based
// on item_sales × recipe components) vs what actually moved (baseline
// + receives − wastes − current on-hand). Positive variance = used
// MORE than expected → shrinkage / over-portioning / theft. Negative
// = used LESS → under-portioning or sales not all rung up.
//
// Color tiers (centralized in variance.js):
//   ≤10%   green  — within normal portioning drift
//   ≤25%   amber  — monitor
//   ≤50%   red    — investigate
//   >50%   deep red — likely shrinkage or process error
//
// Empty-state branches: no_baseline (Set baseline first),
// no_recipes (link recipes to menu items), no_sales (import Item
// Sales). Each state surfaces an actionable next step.
export default function VarianceScreen() {
  const { result, loading, period, setPeriod, refresh } = useVarianceData()
  const [expandedRow, setExpandedRow] = useState(null)

  function setStart(v) {
    setPeriod({ ...period, period_start: v })
  }
  function setEnd(v) {
    setPeriod({ ...period, period_end: v })
  }

  // Headline rollup — sum of $ variance across all rows. Drives the
  // hero card so the manager has a single dollar number to react to.
  const summary = useMemo(() => {
    const rows = result?.rows ?? []
    let total_theoretical = 0, total_actual = 0, total_variance = 0
    let red_count = 0
    for (const r of rows) {
      total_theoretical += r.theoretical_cost
      total_actual      += r.actual_cost
      total_variance    += r.variance_cost
      const tier = varianceTier(r.variance_pct)
      if (tier === 'red' || tier === 'deep-red') red_count++
    }
    return { total_theoretical, total_actual, total_variance, red_count, row_count: rows.length }
  }, [result])

  if (loading && !result) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={5} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">
        {/* ---------- Header / explainer ------------------------ */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Variance report"
        >
          <div className="text-xs font-medium text-black/55">
            Theoretical vs Actual usage
          </div>
          <p className="mt-1 text-xs text-black/60 leading-snug">
            What your recipes SHOULD have used vs what actually moved.
            Big positive variance = shrinkage, theft, or over-portioning.
            Big negative = sales not all rung up, or under-portioning.
          </p>

          {/* Date range */}
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="var-start">From</label>
              <input
                id="var-start"
                type="date"
                value={period.period_start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="var-end">To</label>
              <input
                id="var-end"
                type="date"
                value={period.period_end}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <PresetButton label="Last 7 days"   onClick={() => setPeriod(presetDays(7))} />
            <PresetButton label="Last 14 days"  onClick={() => setPeriod(presetDays(14))} />
            <PresetButton label="Last 30 days"  onClick={() => setPeriod(presetDays(30))} />
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="ml-auto rounded-md border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              {loading ? 'Refreshing\u2026' : 'Refresh'}
            </button>
          </div>
        </section>

        {/* ---------- Status banners --------------------------- */}
        {result?.status === 'no_baseline' && (
          <Banner tone="amber" title="No inventory baseline yet">
            Variance compares against a starting on-hand snapshot. Tap
            <span className="font-medium"> Settings → Set baseline </span>
            to mark the start of your reporting period (usually right
            after a stocktake on the last day of the week).
          </Banner>
        )}
        {result?.status === 'no_recipes' && (
          <Banner tone="amber" title="No recipes linked to menu items">
            Variance needs at least one recipe whose name matches a menu
            item in your catalog. Build a recipe from
            <span className="font-medium"> Settings → AI Build Recipe </span>
            named after the dish — it auto-links if the name matches.
          </Banner>
        )}
        {result?.status === 'no_sales' && (
          <Banner tone="amber" title="No item sales imported for this period">
            Variance multiplies sales × recipes. Import a Toast Item Sales
            CSV at <span className="font-medium"> Settings → Item sales → + Import CSV</span>.
          </Banner>
        )}

        {/* ---------- Summary tile ----------------------------- */}
        {result?.status === 'ready' && (
          <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
            <div className="text-xs font-medium text-black/55">
              {period.period_start} → {period.period_end}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <SummaryTile
                label="Theoretical"
                value={formatMoney(summary.total_theoretical)}
                hint={'what recipes\u2009×\u2009sales'}
              />
              <SummaryTile
                label="Actual"
                value={formatMoney(summary.total_actual)}
                hint={'baseline + recv \u2212 waste \u2212 now'}
              />
              <SummaryTile
                label="Variance"
                value={(summary.total_variance >= 0 ? '+' : '') + formatMoney(summary.total_variance)}
                hint={summary.total_variance > 0 ? 'used more' : summary.total_variance < 0 ? 'used less' : 'right on'}
                emphasized={Math.abs(summary.total_variance) > 0.01}
                tone={Math.abs(summary.total_variance) > 0 && summary.red_count > 0 ? 'red' : 'green'}
              />
            </div>
            {summary.red_count > 0 && (
              <p className="mt-2 text-xs text-red-700 font-medium">
                {summary.red_count} item{summary.red_count === 1 ? '' : 's'} with significant variance — review below.
              </p>
            )}
            {(result.unlinked_recipe_count > 0 || result.unmatched_sales_count > 0) && (
              <p className="mt-2 text-xs text-black/55 leading-snug">
                Note: {result.unlinked_recipe_count > 0 && (
                  <>{result.unlinked_recipe_count} recipe{result.unlinked_recipe_count === 1 ? '' : 's'} skipped (not linked to a menu item)</>
                )}
                {result.unlinked_recipe_count > 0 && result.unmatched_sales_count > 0 && ' \u00b7 '}
                {result.unmatched_sales_count > 0 && (
                  <>{result.unmatched_sales_count} sale row{result.unmatched_sales_count === 1 ? '' : 's'} unmatched to catalog</>
                )}.
              </p>
            )}
          </section>
        )}

        {/* ---------- Per-item variance table ------------------ */}
        {result?.status === 'ready' && summary.row_count > 0 && (
          <section className="mx-4 mt-4" aria-label="Per-item variance">
            <div className="mb-2 text-base font-medium">
              By item ({summary.row_count})
            </div>
            <ul className="overflow-hidden rounded-xl border border-black/10">
              {result.rows.map((row) => {
                const tier   = varianceTier(row.variance_pct)
                const tonePalette = TONES[tier]
                const isOpen = expandedRow === row.item_id
                const sign = row.variance_qty > 0 ? '+' : ''
                return (
                  <li key={row.item_id} className="border-b border-black/5 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setExpandedRow(isOpen ? null : row.item_id)}
                      className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-blue-50/40"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-medium">
                          {row.item_name}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-black/55 tabular-nums">
                          theoretical {formatQty(row.theoretical_qty, row.unit)} ·
                          actual {formatQty(row.actual_qty, row.unit)}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className={`inline-block rounded px-2 py-0.5 text-xs font-medium tabular-nums ${tonePalette.badge}`}>
                          {sign}{formatQty(row.variance_qty, row.unit)}
                        </div>
                        <div className={`mt-0.5 text-xs font-medium tabular-nums ${tonePalette.text}`}>
                          {sign}{formatMoney(row.variance_cost)}
                          {' \u00b7 '}
                          {isFinite(row.variance_pct)
                            ? `${(row.variance_pct * 100).toFixed(0)}%`
                            : '—'}
                        </div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-black/5 bg-black/[0.02] px-4 py-3 text-xs">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
                          <Stat label="Baseline"  value={formatQty(row.baseline_qty, row.unit)} />
                          <Stat label="Now on hand" value={formatQty(row.current_qty, row.unit)} />
                          <Stat label="Received"  value={formatQty(row.received_qty, row.unit)} />
                          <Stat label="Wasted"    value={formatQty(row.wasted_qty,   row.unit)} />
                        </div>
                        {!row.has_baseline && (
                          <p className="mt-2 text-amber-700 font-medium">
                            No baseline before period start — actual side starts at 0.
                          </p>
                        )}

                        {row.contributing_recipes.length > 0 && (
                          <div className="mt-3">
                            <div className="font-medium text-black/65 font-medium text-[10px]">
                              Contributing recipes
                            </div>
                            <ul className="mt-1 space-y-1">
                              {row.contributing_recipes.map((cr) => (
                                <li key={cr.recipe_id} className="leading-snug">
                                  <span className="font-medium">{cr.recipe_name}</span>{' '}
                                  <span className="text-black/55">
                                    · sold {cr.qty_sold_period} ·{' '}
                                    {cr.per_serving_qty.toFixed(3)} {row.unit}/serving
                                    {' = '}
                                    {formatQty(cr.contributed_qty, row.unit)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )}

        {result?.status === 'ready' && summary.row_count === 0 && (
          <div className="mx-4 mt-4 rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
            No matching activity in this window. Either no recipes consume
            inventory items in your catalog, or no item sales linked up.
          </div>
        )}
      </main>
    </div>
  )
}

// ---------- Subcomponents ------------------------------------

const TONES = {
  'green':    { badge: 'bg-emerald-100 text-emerald-800', text: 'text-emerald-700' },
  'amber':    { badge: 'bg-amber-100   text-amber-900',   text: 'text-amber-700' },
  'red':      { badge: 'bg-red-100     text-red-800',     text: 'text-red-700' },
  'deep-red': { badge: 'bg-red-600     text-white',       text: 'text-red-700 font-medium' },
}

function PresetButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-black/10 bg-white px-2.5 py-1 text-xs font-medium text-black/70 hover:bg-blue-50 hover:text-blue-700"
    >
      {label}
    </button>
  )
}

function SummaryTile({ label, value, hint, emphasized, tone }) {
  const ring = !emphasized ? 'border-black/10 bg-black/[0.02]'
    : tone === 'red'  ? 'border-red-200 bg-red-50'
    : 'border-emerald-200 bg-emerald-50/40'
  const valColor = !emphasized ? 'text-black/85'
    : tone === 'red' ? 'text-red-700'
    : 'text-emerald-800'
  return (
    <div className={`rounded-xl border p-2 ${ring}`}>
      <div className="text-[10px] font-medium text-black/55">
        {label}
      </div>
      <div className={`mt-0.5 text-base font-medium tabular-nums ${valColor}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-black/55 leading-tight">
          {hint}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-black/55">{label}</span>
      <span className="font-medium text-black/85">{value}</span>
    </div>
  )
}

function Banner({ tone, title, children }) {
  const palette = tone === 'amber'
    ? 'border-amber-200 bg-amber-50 text-amber-900'
    : 'border-red-200 bg-red-50 text-red-900'
  return (
    <section className={`mx-4 mt-4 rounded-2xl border p-4 ${palette}`}>
      <div className="text-sm font-medium">{title}</div>
      <p className="mt-1 text-xs leading-relaxed">{children}</p>
    </section>
  )
}

function presetDays(n) {
  const today = new Date()
  const start = new Date(today.getTime() - (n - 1) * 24 * 60 * 60 * 1000)
  return { period_start: ymd(start), period_end: ymd(today) }
}
function ymd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
